import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import toast from 'react-hot-toast';

import { QUEUE_STORAGE_KEY, offlineQueue, type OfflineQueue, type QueueItem } from '../lib/offlineQueue';

/** Kým niečo čaká a sieť sa tvári, že je, skúša sa znova po tejto dobe. */
const RETRY_EVERY_MS = 30_000;

/**
 * Zapojenie fronty do rámu appky: kto je prihlásený a kedy sa má odosielať.
 *
 * Odosiela sa pri štarte, keď sa sieť vráti, keď sa karta znova ukáže a
 * pravidelne, kým niečo čaká — udalosť `online` totiž nepríde, keď sieť síce
 * „je", ale nič neprechádza (wifi bez internetu), a fronta by stála.
 */
export const useOfflineQueueSync = (userId: number | null, queue: OfflineQueue = offlineQueue()) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    queue.setUser(userId);
    if (userId === null) {
      return undefined;
    }

    const refreshAfter = (sent: QueueItem[]) => {
      const keys = new Set<string>();
      for (const { action } of sent) {
        if (action.kind === 'taskComment') {
          keys.add(JSON.stringify(['tasks', action.taskId, 'comments']));
          keys.add(JSON.stringify(['tasks']));
        } else if (action.kind === 'chatMessage') {
          keys.add(JSON.stringify(['messages']));
        } else if (action.kind === 'note') {
          keys.add(JSON.stringify(['notes']));
        } else {
          keys.add(JSON.stringify(['tasks']));
        }
      }
      for (const key of keys) {
        void queryClient.invalidateQueries({ queryKey: JSON.parse(key) as unknown[] });
      }
    };

    const run = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return;
      }
      if (queue.pendingCount() === 0) {
        return;
      }
      void queue.flush().then((result) => {
        if (result.sent.length > 0) {
          refreshAfter(result.sent);
        }
        if (result.failed.length > 0) {
          // Jedno upozornenie; podrobnosti a voľba, čo s tým, sú v pruhu fronty.
          toast.error(
            result.failed.length === 1
              ? 'Jednu odloženú zmenu server odmietol.'
              : `Odložené zmeny, ktoré server odmietol: ${result.failed.length}.`,
          );
        }
      });
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        run();
      }
    };

    run();
    window.addEventListener('online', run);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(run, RETRY_EVERY_MS);
    return () => {
      window.removeEventListener('online', run);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
      // Po odhlásení nesmie fronta posielať (ani prijímať) pod starým menom.
      queue.setUser(null);
    };
  }, [userId, queue, queryClient]);
};

/** Položky fronty pre aktuálneho človeka, živo. */
export const useOfflineQueueItems = (queue: OfflineQueue = offlineQueue()): QueueItem[] => {
  // Snímka je počítadlo zmien, nie pole — `items()` vracia zakaždým nové pole
  // a useSyncExternalStore by sa s ním prekresľoval donekonečna.
  useSyncExternalStore(subscribeWithOtherTabs(queue), () => queue.version(), () => 0);
  return queue.items();
};

const subscribers = new WeakMap<OfflineQueue, (listener: () => void) => () => void>();

/** Odber zmien vrátane tých z inej karty (udalosť `storage`). */
const subscribeWithOtherTabs = (queue: OfflineQueue) => {
  let subscribe = subscribers.get(queue);
  if (!subscribe) {
    subscribe = (listener: () => void) => {
      const off = queue.subscribe(listener);
      const onStorage = (event: StorageEvent) => {
        if (event.key === QUEUE_STORAGE_KEY) {
          queue.refresh();
        }
      };
      window.addEventListener('storage', onStorage);
      return () => {
        off();
        window.removeEventListener('storage', onStorage);
      };
    };
    subscribers.set(queue, subscribe);
  }
  return subscribe;
};
